import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Divider,
  Avatar,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useAuthStore } from '../store/auth.store';
import { useSiteStore } from '../store/site.store';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Post-login screen for agency admin users.
 * Shown when profile = 'agency-admin' and a tenant has not yet been selected.
 * The user selects one client tenant; the frontend calls /agency/switch-tenant,
 * which re-issues a JWT with that tenantId baked in. All subsequent API calls
 * behave identically to a regular customer for that tenant.
 */
export const TenantSwitcherScreen = () => {
  const { user, switchTenant, logout } = useAuthStore();
  const { clearSite, setSites } = useSiteStore();
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const allowedTenants = user?.allowedTenants ?? [];

  const effectiveTenants = allowedTenants;


  // Agency branding comes directly from auth state - populated at login, no extra call needed
  const agencyLogoUrl = user?.agencyLogoUrl;
  const agencyName = user?.agencyName;

  const handleProceed = async () => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    try {
      await switchTenant(selectedTenantId);
      // Clear stale site context from the old tenant
      clearSite();
      setSites([]);
      // Invalidate the sites query so AppLayout fetches fresh for the new tenant —
      // mirrors exactly what happens when a customer logs in for the first time.
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 6, marginBottom: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Avatar
              src={agencyLogoUrl || undefined}
              alt={agencyName || user?.agencyCode || 'Agency'}
              sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.2rem', fontWeight: 700, mx: 'auto', mb: 1 }}
            >
              {(user?.agencyCode || 'A').charAt(0).toUpperCase()}
            </Avatar>
            <Typography component="h1" variant="h5" gutterBottom fontWeight={600}>
              {user?.profile === 'agency-admin' ? (agencyName ?? user?.agencyCode ?? 'Agency') : 'Select Workspace'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.profile === 'agency-admin'
                ? 'Select the client website you want to manage this session'
                : 'You have access to multiple workspaces. Select one to continue.'}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* No tenants message */}
          {effectiveTenants.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No client websites are linked to your agency yet. Contact your platform admin.
            </Alert>
          )}

          {/* Tenant list */}
          {effectiveTenants.length > 0 && (
            <RadioGroup
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
            >
              {effectiveTenants.map((tenant) => (
                <FormControlLabel
                  key={tenant.id}
                  value={tenant.id}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>{tenant.name}</Typography>
                    </Box>
                  }
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: selectedTenantId === tenant.id ? 'primary.main' : 'divider',
                    bgcolor: selectedTenantId === tenant.id ? 'primary.50' : 'transparent',
                    width: '100%',
                    ml: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                />
              ))}
            </RadioGroup>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}



          {/* Action button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!selectedTenantId || loading || effectiveTenants.length === 0}
            onClick={handleProceed}
            sx={{ mt: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : 'View Website →'}
          </Button>

          <Button
            fullWidth
            variant="text"
            size="medium"
            disabled={loading}
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            ← Back to Login
          </Button>

        </Paper>
      </Box>
    </Container>
  );
};

export default TenantSwitcherScreen;
